<template>
    <div class="mc-root stampa-odr-page">
        <main class="mc-page">
            <mc-page-header title="Stampa ODR" subtitle="Ordini di rilavorazione non ancora stampati">
                <template #actions>
                    <v-btn text color="primary" :to="{ name: 'qualita-mail-segnalazioni' }">
                        Mail Segnalazioni
                    </v-btn>
                    <v-btn outlined :loading="loading" @click="loadOrders">
                        <v-icon left>mdi-refresh</v-icon>Aggiorna
                    </v-btn>
                </template>
            </mc-page-header>

            <div v-if="localPreview" class="stampa-odr-note" role="status">
                <v-icon aria-hidden="true">mdi-information-outline</v-icon>
                <span>Preview locale: la stampa è disattivata.</span>
            </div>
            <div v-if="pollError" class="stampa-odr-note stampa-odr-note--warning" role="alert">
                <v-icon aria-hidden="true">mdi-alert</v-icon>
                <span>{{ pollError }} Il controllo automatico riproverà tra poco.</span>
            </div>

            <section class="mc-card stampa-odr-filters" aria-label="Ricerca e stampante">
                <div class="stampa-odr-fields" role="search" aria-label="Cerca ODR">
                    <div class="stampa-odr-field">
                        <label for="stampa-odr-search" class="t-label">Cerca ODR, articolo, riferimento o cliente</label>
                    <v-text-field
                        id="stampa-odr-search"
                        v-model.trim="search"
                        aria-label="Cerca ODR, articolo, riferimento o cliente"
                        prepend-inner-icon="mdi-magnify"
                        clearable
                        hide-details="auto"
                        dense
                        outlined
                    />
                    </div>
                    <div class="stampa-odr-field">
                        <label for="stampa-odr-printer" class="t-label">Stampante A4</label>
                    <v-select
                        id="stampa-odr-printer"
                        v-model="printerId"
                        :items="printers"
                        item-text="label"
                        item-value="id"
                        aria-label="Stampante A4"
                        :loading="printersLoading"
                        :error-messages="printerError ? [printerError] : []"
                        hide-details="auto"
                        dense
                        outlined
                    />
                    </div>
                </div>
                <div v-if="!loading && !error" class="stampa-odr-summary" aria-live="polite">
                    <div class="stampa-odr-count">
                        <strong class="t-num-md">{{ displayNumber(filteredOrders.length) }}</strong>
                        <span>ODR da stampare</span>
                    </div>
                    <div v-if="filteredWaitingOrders.length" class="stampa-odr-waiting t-caption">
                        <v-progress-circular
                            indeterminate
                            size="16"
                            width="2"
                            aria-label="Controllo ODR in attesa"
                        />
                        {{ displayNumber(filteredWaitingOrders.length) }} in attesa · controllo ogni 10 s
                    </div>
                </div>
            </section>

            <section v-if="error || (loading && !orders.length) || !filteredOrders.length" class="mc-card">
                <mc-state-message v-if="error" state="error" :title="error" @retry="loadOrders" />
                <mc-state-message v-else-if="loading && !orders.length" state="loading" />
                <mc-state-message v-else-if="!orders.length" state="empty" title="Nessun ODR da stampare." />
                <mc-state-message v-else state="empty" title="Nessun ODR corrisponde alla ricerca." />
            </section>

            <stampa-odr-group
                v-for="group in groups"
                :key="group.name"
                :group="group"
                :printer-id="printerId"
                :local-preview="localPreview"
                :printing="printing"
                @print-group="openGroupConfirmation(group)"
                @print-order="openConfirmation"
            />

            <v-dialog v-model="confirmDialog" max-width="520" persistent aria-labelledby="odr-confirm-title">
                <v-card v-if="selectedOrders.length" class="mc-root mc-dialog stampa-odr-dialog">
                    <div class="mc-dialog__head">
                        <h2 id="odr-confirm-title" class="mc-dialog__title">Conferma stampa ODR</h2>
                    </div>
                    <div class="mc-dialog__body stampa-odr-confirm-body">
                        <div v-if="selectedOrders.length === 1"><strong>Ordine:</strong> <span class="mc-code">{{ selectedOrders[0].ordine }}</span></div>
                        <div v-if="selectedOrders.length === 1"><strong>Articolo:</strong> <span class="mc-code">{{ selectedOrders[0].articolo }}</span></div>
                        <div v-else><strong>ODR da accodare:</strong> {{ displayNumber(selectedOrders.length) }} nella fase {{ selectedGroupName }}</div>
                        <div><strong>Stampante:</strong> {{ selectedPrinterLabel }}</div>
                        <p>{{ selectedOrders.length === 1 ? "L'ordine verrà inviato alla coda di stampa." : "Gli ordini verranno inviati uno alla volta. In caso di errore la sequenza si interrompe." }}</p>
                    </div>
                    <div class="mc-dialog__foot">
                        <v-btn text :disabled="Boolean(printing)" @click="confirmDialog = false">Annulla</v-btn>
                        <v-btn depressed color="primary" :loading="Boolean(printing)" @click="printSelected">Conferma e stampa</v-btn>
                    </div>
                </v-card>
            </v-dialog>
        </main>
    </div>
</template>

<script>
import McPageHeader from "@/assets/mecom/vue/McPageHeader.vue";
import McStateMessage from "@/assets/mecom/vue/McStateMessage.vue";
import { number } from "@/assets/mecom/js/format.mjs";
import StampaOdrGroup from "./stampa-odr/StampaOdrGroup.vue";
import {
    isStampaOdrPrintable,
    needsStampaOdrPolling,
    STAMPA_ODR_POLL_INTERVAL_MS,
} from "./stampaOdrStatus.mjs";

export default {
    name: "StampaOdr",
    components: { McPageHeader, McStateMessage, StampaOdrGroup },
    data: () => ({
        orders: [],
        printers: [],
        printerId: null,
        search: "",
        loading: false,
        printersLoading: false,
        printing: null,
        error: "",
        printerError: "",
        pollError: "",
        pollTimer: null,
        checkingWaitingOrders: false,
        localPreview: false,
        confirmDialog: false,
        selectedOrders: [],
        selectedGroupName: "",
    }),
    computed: {
        filteredOrders() {
            const term = (this.search || "").toLocaleLowerCase().trim();
            if (!term) return this.orders;
            return this.orders.filter((order) => [
                order.ordine, order.articolo, order.rif, order.cliente_nome,
            ].some((value) => String(value || "").toLocaleLowerCase().includes(term)));
        },
        groups() {
            const groups = new Map();
            this.filteredOrders.forEach((order) => {
                const name = order.prima_fase || "";
                if (!groups.has(name)) groups.set(name, []);
                groups.get(name).push(order);
            });
            return Array.from(groups, ([name, orders]) => ({ name, orders }));
        },
        selectedPrinterLabel() {
            return this.printers.find((printer) => printer.id === this.printerId)?.label || "—";
        },
        waitingOrders() {
            return this.orders.filter(needsStampaOdrPolling);
        },
        filteredWaitingOrders() {
            return this.filteredOrders.filter(needsStampaOdrPolling);
        },
    },
    mounted() {
        document.title = "Work - Stampa ODR";
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
        this.loadOrders();
        this.loadPrinters();
    },
    beforeDestroy() {
        this.clearPolling();
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    },
    methods: {
        displayNumber: number,
        async loadOrders() {
            this.clearPolling();
            this.loading = true;
            this.error = "";
            this.pollError = "";
            try {
                const response = await this.$work.get("/qualita/stampa-odr/");
                this.orders = response.data.ordini || [];
                this.localPreview = Boolean(response.data.local_preview);
            } catch (error) {
                this.orders = [];
                this.error = error.response?.data?.detail || "Impossibile caricare gli ODR. Riprova.";
            } finally {
                this.loading = false;
                this.schedulePolling();
            }
        },
        async loadPrinters() {
            this.printersLoading = true;
            this.printerError = "";
            try {
                const response = await this.$work.get("/spooler/stampanti/A4/");
                this.printers = response.data || [];
                const saved = await this.$vlf.getItem("stampa_odr_stampante");
                if (this.printers.some((printer) => printer.id === saved)) this.printerId = saved;
            } catch (error) {
                this.printers = [];
                this.printerError = "Stampanti non disponibili. Aggiorna la pagina.";
            } finally {
                this.printersLoading = false;
            }
        },
        openConfirmation(order) {
            if (!this.printerId || !this.isPrintable(order)) return;
            this.selectedOrders = [order];
            this.selectedGroupName = order.prima_fase || "Fase non indicata";
            this.confirmDialog = true;
        },
        isPrintable(order) {
            return isStampaOdrPrintable(order);
        },
        needsPolling(order) {
            return needsStampaOdrPolling(order);
        },
        printableOrders(orders) {
            return orders.filter(this.isPrintable);
        },
        openGroupConfirmation(group) {
            if (!this.printerId) return;
            this.selectedOrders = this.printableOrders(group.orders);
            if (!this.selectedOrders.length) return;
            this.selectedGroupName = group.name || "Fase non indicata";
            this.confirmDialog = true;
        },
        clearPolling() {
            if (this.pollTimer) window.clearTimeout(this.pollTimer);
            this.pollTimer = null;
        },
        schedulePolling() {
            this.clearPolling();
            if (!this.waitingOrders.length) return;
            this.pollTimer = window.setTimeout(
                () => this.refreshWaitingOrders(),
                STAMPA_ODR_POLL_INTERVAL_MS,
            );
        },
        async refreshWaitingOrders() {
            this.clearPolling();
            if (!this.waitingOrders.length) return;
            if (document.hidden) {
                this.schedulePolling();
                return;
            }

            this.checkingWaitingOrders = true;
            this.pollError = "";
            const waitingOrders = [...this.waitingOrders];
            const checks = await Promise.allSettled(waitingOrders.map((order) => (
                this.$work.get(`/qualita/stampa-odr/${encodeURIComponent(order.ordine)}/stato/`)
            )));
            let reloadList = false;
            checks.forEach((check, index) => {
                if (check.status !== "fulfilled") {
                    this.pollError = "Impossibile aggiornare uno o più ODR in attesa.";
                    return;
                }
                const state = check.value.data;
                const order = waitingOrders[index];
                if (!state.da_stampare) {
                    reloadList = true;
                    return;
                }
                order.stampabile = state.stampabile;
                order.stampabile_msg = state.stampabile_msg;
                order.in_coda = state.in_coda;
            });
            this.checkingWaitingOrders = false;

            if (reloadList) {
                await this.loadOrders();
                return;
            }
            this.schedulePolling();
        },
        handleVisibilityChange() {
            if (document.hidden) {
                this.clearPolling();
            } else if (this.waitingOrders.length && !this.checkingWaitingOrders) {
                this.refreshWaitingOrders();
            }
        },
        async printSelected() {
            if (!this.selectedOrders.length || !this.printerId || this.printing) return;
            const orders = [...this.selectedOrders];
            this.printing = orders.length > 1 ? "batch" : orders[0].ordine;
            let queued = 0;
            let failure = "";
            let waitingFailure = false;
            try {
                for (const order of orders) {
                    try {
                        await this.$work.post("/qualita/stampa-odr/", {
                            ordine: order.ordine,
                            stampante: this.printerId,
                        }, true);
                        order.in_coda = true;
                        queued += 1;
                    } catch (error) {
                        const message = error.response?.data?.detail || "Esito non verificato. Controlla la coda prima di riprovare.";
                        waitingFailure = error.response?.data?.code === "not_printable";
                        if (waitingFailure) {
                            order.stampabile = 1;
                            order.stampabile_msg = message;
                        } else if (!error.response || error.response.status >= 500) {
                            order.in_coda = true;
                        }
                        failure = `${order.ordine}: ${message}`;
                        break;
                    }
                }
                this.confirmDialog = false;
                if (failure && waitingFailure) {
                    this.$toast.warning(`${failure} Controllo automatico attivo.`, { icon: "mdi-clock-outline", timeout: 6000 });
                    this.schedulePolling();
                } else if (failure) {
                    this.$toast.error(`${failure} ${queued} ODR già accodati.`, { icon: "mdi-alert", timeout: 6000 });
                } else if (queued) {
                    this.$toast.success(`${queued} ODR accodati`, { icon: "mdi-check", timeout: 4000 });
                }
                try {
                    await this.$vlf.setItem("stampa_odr_stampante", this.printerId);
                } catch {
                    // La preferenza locale non cambia l'esito della stampa già accodata.
                }
            } finally {
                this.printing = null;
            }
        },
    },
};
</script>

<style scoped>
.stampa-odr-page { min-width: 0; }
.stampa-odr-fields { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: var(--space-4); }
.stampa-odr-field { display: flex; flex-direction: column; justify-content: flex-end; gap: var(--space-2); min-width: 0; }
.stampa-odr-field label { color: var(--ink-2); }
.stampa-odr-summary { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--space-3); margin-top: var(--space-4); padding-top: var(--space-4); border-top: 1px solid var(--border); }
.stampa-odr-count, .stampa-odr-waiting, .stampa-odr-note { display: flex; align-items: center; gap: var(--space-2); }
.stampa-odr-count { color: var(--ink-2); }
.stampa-odr-count strong { color: var(--ink); }
.stampa-odr-waiting { color: var(--warn-ink); }
.stampa-odr-note { padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); background: var(--info-soft); color: var(--info-ink); }
.stampa-odr-note--warning { background: var(--warn-soft); color: var(--warn-ink); }
.stampa-odr-note .v-icon { color: inherit; }
.stampa-odr-confirm-body { overflow-wrap: anywhere; }
.stampa-odr-confirm-body p { margin: var(--space-4) 0 0; }
.stampa-odr-page ::v-deep .v-input input { color: var(--ink); }
.stampa-odr-page ::v-deep .v-select__selection { color: var(--ink); }
.stampa-odr-page ::v-deep .v-btn--text.primary--text { color: var(--brand-ink) !important; }
.stampa-odr-dialog .v-btn.primary { background: var(--brand) !important; color: var(--on-brand) !important; }
@media (max-width: 640px) {
    .stampa-odr-fields { grid-template-columns: minmax(0, 1fr); gap: var(--space-5); }
}
</style>
